import { Injectable, inject } from '@angular/core';
import { ItemManagementService } from "@app/cem/project/services/item-management.service";
import { LookupService } from "@app/cem/project/services/lookup.service";
import { IStandardConfigSaveRequest, StandardConfigManagementService } from "@app/cem/project/services/standard-config-management.service";
import { ErrorService } from '@app/common/services/error.service';
import { PlexEnvironment } from '@app/env';
import { IEquationDto } from "@app/shared/models/dtos/equation";
import { IFunctionDto } from '@app/shared/models/dtos/function';
import { IItemDto } from "@app/shared/models/dtos/item";
import { IProjectTypeDto } from '@app/shared/models/dtos/project-type';
import { IProjectTypePhasesAndStepsDto } from "@app/shared/models/dtos/project-type-phases-and-steps";
import { IQtyTypeDto } from "@app/shared/models/dtos/qty-type";
import { IStandardConfigDto } from "@app/shared/models/dtos/standard-config";
import { IStandardConfigItemDto } from "@app/shared/models/dtos/standard-config-item";
import { StandardConfigItemDtoValidator } from "@app/shared/models/validators/standard-config-item-validator";
import { StandardConfigDtoValidator } from "@app/shared/models/validators/standard-config-validator";
import { GridApi, RowDropZoneParams } from 'ag-grid-community';
import { ProjectSubTypeId, ProjectTypeId } from 'app/shared/data/project-enums';
import moment from 'moment';
import { Observable, Subject } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class StdCfgSharedServiceService {
  private lookupService = inject(LookupService);
  private standardConfigManagementService = inject(StandardConfigManagementService);
  private itemManagementService = inject(ItemManagementService);
  private errorService = inject(ErrorService);
  private environment = inject(PlexEnvironment);

  public IProjectTypeDtos: Array<IProjectTypeDto> = new Array<IProjectTypeDto>();
  public IFunctionDtos: Array<IFunctionDto> = new Array<IFunctionDto>();
  public IProjectTypePhasesAndStepsDto: Array<IProjectTypePhasesAndStepsDto> = new Array<IProjectTypePhasesAndStepsDto>();
  public IStandardConfigDtos: Array<IStandardConfigDto> = new Array<IStandardConfigDto>();
  public IItemDtos: Array<IItemDto> = new Array<IItemDto>();
  private everythingLoaded$: Subject<boolean> = new Subject();
  public IIQtyTypeDtos: Array<IQtyTypeDto> = new Array<IQtyTypeDto>();
  public IIEquationDtos: Array<IEquationDto> = new Array<IEquationDto>();
  public editMode: boolean = false;
  newItemId = 0;
  newIStandardConfigItemDtoId = 0;
  SelectedFunctionDto$: Subject<IFunctionDto> = new Subject();
  SelectedStandardConfigDto$: Subject<IStandardConfigDto> = new Subject();
  private SelectedStandardConfigItem: IStandardConfigDto;
  public editedEntities: Map<number, IStandardConfigDto> = new Map<any, IStandardConfigDto>();
  public editedIStandardConfigItemDtos: Map<number, IStandardConfigItemDto> = new Map<any, IStandardConfigItemDto>();
  public removedIStandardConfigItemDtos: Map<number, IStandardConfigItemDto> = new Map<any, IStandardConfigItemDto>();

  public ActiveIStandardConfigDtoState: Map<number, IStandardConfigDtoState> = new Map<any, IStandardConfigDtoState>();

  public ValidationErrors: Map<string, Array<string>> = new Map<string, Array<string>>();
  private standardConfigDtoValidator = new StandardConfigDtoValidator();
  private standardConfigItemDtoValidator = new StandardConfigItemDtoValidator();

  private leftApi: GridApi;
  private rightApi: GridApi;
  private leftDropZone: RowDropZoneParams;
  private rightDropZone: RowDropZoneParams;

  IStandardConfigDtos$: Subject<Array<IStandardConfigDto>> = new Subject();

  public async loadLookups() {
    try {
      this.IFunctionDtos = (await this.lookupService.findActiveFunctionDtos()).Results;
      this.IProjectTypeDtos = (await this.lookupService.findProjectTypeDtos()).Results;
      this.IProjectTypePhasesAndStepsDto = (await this.lookupService.findProjectTypePhasesAndStepsDto()).Results;
      this.IIQtyTypeDtos = (await this.standardConfigManagementService.getStandardConfigurationQtyTypes()).Results;
      this.IIEquationDtos = (await this.lookupService.findequations()).Results;
      await this.loadStandardConfigs(null);
      this.IItemDtos = (await this.itemManagementService.findItemDtos()).Results;
      this.everythingLoaded$.next(true);
    }
    catch (error) {
      this.errorService.error("Error loading up data.", error);
    }
  }

  public isItemsLoaded(): Observable<boolean> {
    return this.everythingLoaded$.asObservable();
  }

  public AddOrEditItem(id: number, item: IStandardConfigItemDto): void {
    this.editedIStandardConfigItemDtos.set(id, item);
    this.removedIStandardConfigItemDtos.delete(id);
  }

  public RemoveItem(id: number, item: IStandardConfigItemDto): void {
    this.editedIStandardConfigItemDtos.delete(id);
    this.removedIStandardConfigItemDtos.set(id, item);
  }

  public async loadStandardConfigs(standardConfiguration: IStandardConfigDto): Promise<any> {
    var data = await this.standardConfigManagementService.findStandardConfigDtos(standardConfiguration && standardConfiguration.StandardConfigurationId);

    if (standardConfiguration === null) {
      this.IStandardConfigDtos = data.Results;
      this.setStandardConfigDtos(this.IStandardConfigDtos);
    } else {
      standardConfiguration.StandardConfigurationItemDtos = data.Results[0].StandardConfigurationItemDtos;
    }
    return standardConfiguration;
  }

  public async copyStdConfig(standardConfiguration: IStandardConfigDto): Promise<any> {
    let response = await this.standardConfigManagementService.copyStandardConfigDtos(standardConfiguration);
    return response;
  }

  public getSelectedFunctionDto(): Observable<IFunctionDto> {
    return this.SelectedFunctionDto$.asObservable();
  }

  public async setSelectedFunctionDto(iFunctionDto: IFunctionDto): Promise<void> {
    await this.cancelNewStandardConfigDto();
    this.SelectedFunctionDto$.next(iFunctionDto);
  }

  public getSelectedStandardConfigDto(): Observable<IStandardConfigDto> {
    return this.SelectedStandardConfigDto$.asObservable();
  }

  public async setSelectedStandardConfigDto(istandardConfigDto: IStandardConfigDto): Promise<any> {
    this.SelectedStandardConfigItem = istandardConfigDto;
    if (istandardConfigDto) {
      await this.loadStandardConfigs(istandardConfigDto);
    }

    this.SelectedStandardConfigDto$.next(istandardConfigDto);
  }

  public getStandardConfigDtos(): Observable<Array<IStandardConfigDto>> {
    return this.IStandardConfigDtos$.asObservable();
  }

  public setStandardConfigDtos(IStandardConfigDtos: Array<IStandardConfigDto>): void {
    this.IStandardConfigDtos$.next(IStandardConfigDtos);
  }

  public addNewStandardConfigDto(iFunctionDto: IFunctionDto): IStandardConfigDto {
    this.editMode = true;
    let istandardConfigDto = <IStandardConfigDto>{
      StandardConfigurationId: this.newItemId--
      , FunctionDto: iFunctionDto
      , Name: ''
      , IsActive: true
      , IsDefault: false
      , IsDELDefault: false
      , StandardConfigurationItemDtos: new Array<IStandardConfigItemDto>()
      , FunctionId: iFunctionDto.FunctionId
    };

    this.IStandardConfigDtos.push(istandardConfigDto);
    this.setStandardConfigDtos(this.IStandardConfigDtos);
    return istandardConfigDto;
  }

  public async cancelNewStandardConfigDto(): Promise<void> {
    this.editedEntities = new Map<any, IStandardConfigDto>();
    this.editedIStandardConfigItemDtos = new Map<any, IStandardConfigItemDto>();
    this.removedIStandardConfigItemDtos = new Map<any, IStandardConfigItemDto>();

    this.ValidationErrors = new Map<string, Array<string>>();
    this.newItemId = 0;
    this.newIStandardConfigItemDtoId = 0;
    this.editMode = false;
    //TODO: on cancel we will need to re-get the selected standard config, calling with the id
    if (this.SelectedStandardConfigItem) {
      this.SelectedStandardConfigItem.StandardConfigurationItemDtos = null;
      this.ActiveIStandardConfigDtoState.delete(this.SelectedStandardConfigItem.StandardConfigurationId);
    }

    await this.setSelectedStandardConfigDto(this.SelectedStandardConfigItem);
    await this.loadStandardConfigs(null);
  }

  public async saveChanges(): Promise<boolean> {
    if (this.editedEntities.size === 0 && this.editedIStandardConfigItemDtos.size === 0 && this.removedIStandardConfigItemDtos.size === 0) {
      return true;
    }

    let edited = Array.from(this.editedEntities.values());

    this.Validate(edited);

    if (this.ValidationErrors.size > 0) {
      this.editMode = true;
      return false;
    }

    let iStandardConfigSaveRequest: IStandardConfigSaveRequest = {
      StandardConfigDtos: edited,
      StandardConfigurationItemDtoUpserts: Array.from(this.editedIStandardConfigItemDtos.values()),
      StandardConfigurationItemDtoRemoves: Array.from(this.removedIStandardConfigItemDtos.values())
    };

    try {
      var data = (await this.standardConfigManagementService.upsertStandardConfigDtos(iStandardConfigSaveRequest)).Results;
    }
    catch (error) {
      this.errorService.error('An error occurred saving the standard config', error);
      this.editMode = false;
      return false;
    }

    this.editedEntities = new Map<any, IStandardConfigDto>();
    this.editedIStandardConfigItemDtos = new Map<any, IStandardConfigItemDto>();
    this.removedIStandardConfigItemDtos = new Map<any, IStandardConfigItemDto>();
    this.ValidationErrors = new Map<string, Array<string>>();
    this.newItemId = 0;
    this.newIStandardConfigItemDtoId = 0;
    this.editMode = false;

    if (data.length === 1) {
      //should always be one item
      //apply updated data from saved into edited -- so the object reference stays the same, values get updated
      let newSave = false;
      if (edited[0].StandardConfigurationId <= 0) {
        edited[0].StandardConfigurationId = data[0].StandardConfigurationId;
        newSave = true;
      }
      else {
        this.ActiveIStandardConfigDtoState.delete(data[0].StandardConfigurationId);
      }

      let standardConfigDtoIndex = this.IStandardConfigDtos.findIndex(x => x.StandardConfigurationId == data[0].StandardConfigurationId);
      if (standardConfigDtoIndex >= 0) {
        this.IStandardConfigDtos[standardConfigDtoIndex] = data[0];
      }

      this.setStandardConfigDtos(this.IStandardConfigDtos);

      await this.setSelectedStandardConfigDto(data[0]);
    }
    return true;
  }

  private Validate(array: IStandardConfigDto[]) {
    this.ValidationErrors = new Map<string, Array<string>>();

    array.forEach(element => {
      let obj = this.standardConfigDtoValidator.validate(element);
      const values = Object.values(obj);

      var found = false;
      if (found) {
        values.push('Duplicate Name of "' + element.Name + '" found');
      }
      if (values.length > 0) {
        let messages = new Array<string>();

        values.forEach(message => {
          messages.push(message as string);
        });
        this.ValidationErrors.set('Standard Config with Id of ' + Math.abs(element.StandardConfigurationId).toString() + ' has the following error(s):', messages);
      }
    });

    this.validateNoDuplicateDefault(array, "IsDefault", "Default");
    this.validateAtLeastOneDefault(array, "IsDefault", "Default");

    if (this.environment.showDelawareBasin) {
      this.validateNoDuplicateDefault(array, "IsDELDefault", "DEL Default");
      this.validateAtLeastOneDefault(array, "IsDELDefault", "DEL Default");
    }

    let iStandardConfigItemDtos = Array.from(this.editedIStandardConfigItemDtos.values());
    iStandardConfigItemDtos.forEach(element => {
      let obj = this.standardConfigItemDtoValidator.validate(element);
      const values = Object.values(obj);

      if (values.length > 0) {
        let messages = new Array<string>();

        values.forEach(message => {
          messages.push(message as string);
        });
        this.ValidationErrors.set((element.Phase ? element.Phase : '') + ' ' + (element.Step ? element.Step : '') + ' - Item with Id of ' + Math.abs(element.ItemDto.ItemNumber).toString() + ' has the following error(s):', messages);
      }
    });
  }

  public validateNoDuplicateDefault(array: IStandardConfigDto[], column: keyof IStandardConfigDto, label: string) {
    array.forEach(editedEntitiesElement => {
      let badList = this.IStandardConfigDtos.filter(s =>
        s[column] === editedEntitiesElement[column] === true &&
        s.FunctionId === editedEntitiesElement.FunctionId &&
        s.ProjectTypeId === editedEntitiesElement.ProjectTypeId &&
        s.ConstructionTypeId === editedEntitiesElement.ConstructionTypeId &&
        s.ProjectSubTypeId === editedEntitiesElement.ProjectSubTypeId &&
        moment(s.EffectiveDate).isSame(editedEntitiesElement.EffectiveDate)
      );
      if (badList.length > 1) {
        this.ValidationErrors.set('Standard Config with Id of ' + Math.abs(editedEntitiesElement.StandardConfigurationId).toString() + ' has the following error(s):',
          [`${label} Standard Configs must have unique Function Ids, Project Types, Project Sub Types, and Effective Dates`]);
      }
    });
  }

  public validateAtLeastOneDefault(array: IStandardConfigDto[], column: keyof IStandardConfigDto, label: string) {
    let requiredDefaultProjectTypeIdsCombinations = [[ProjectTypeId.WellHookup, null], [ProjectTypeId.DrillOilGasWell, ProjectSubTypeId.DrillHorizontal], [ProjectTypeId.DrillOilGasWell, ProjectSubTypeId.DrillVertical], [ProjectTypeId.CompletePostStimOAG, null], [ProjectTypeId.CompleteStimOAG, null], [ProjectTypeId.Flowline, null], [ProjectTypeId.WellPadFacility, null]];
    let badChanges: Array<IStandardConfigDto> = new Array<IStandardConfigDto>();
    requiredDefaultProjectTypeIdsCombinations.forEach(set => {
      let standardConfigsOfInterest = this.IStandardConfigDtos.filter(s => s.ProjectTypeId === set[0] &&
        s.ProjectSubTypeId === set[1] &&
        s[column]);
      let editedstandardConfigsOfInterest = array.filter(s => s.ProjectTypeId === set[0] &&
        s.ProjectSubTypeId === set[1]);
      if (standardConfigsOfInterest.length < 1) {
        editedstandardConfigsOfInterest.forEach(s => badChanges.push(s));
      }
    });
    if (badChanges) {
      badChanges.forEach(stdConfig => {
        this.ValidationErrors.set('Standard Config with Id of ' + Math.abs(stdConfig.StandardConfigurationId).toString() + ' has the following error(s):',
          [`There must be at least one '${label}' Standard Config for Combination of Project: ` +
            stdConfig.ProjectTypeDto.Description +
            ' and Project SubType:  ' +
            (stdConfig.ProjectSubTypeDto ? stdConfig.ProjectSubTypeDto.Description : ' ') +
            '.']);
      });
    }
  }

  public setItemGridApi(itemGridApi: GridApi): void {
    this.setGridApi('leftApi', itemGridApi);
  }

  public connectItemGridToStdConfigItemGrid(stdConfigGridApi: GridApi) {
    this.setGridApi('rightApi', stdConfigGridApi);
  }

  private setGridApi(apiField: 'leftApi' | 'rightApi', gridApi: GridApi): void {
    if (this[apiField] === gridApi) return;
    this.removeGridDropZones();
    this[apiField] = gridApi;
    if (gridApi) {
      gridApi.addEventListener('gridPreDestroyed', () => {
        if (this[apiField] !== gridApi) return;
        this.removeGridDropZones();
        this[apiField] = null;
      });
    }
    this.connectGridDropZones();
  }

  private connectGridDropZones(): void {
    if (!this.leftApi || !this.rightApi || this.leftApi.isDestroyed() || this.rightApi.isDestroyed()) return;
    this.leftDropZone = this.addGridDropZone('Left', this.leftApi);
    this.rightDropZone = this.addGridDropZone('Right', this.rightApi);
  }

  private removeGridDropZones(): void {
    if (this.leftDropZone && this.leftApi && !this.leftApi.isDestroyed()) {
      this.leftApi.removeRowDropZone(this.leftDropZone);
    }
    if (this.rightDropZone && this.rightApi && !this.rightApi.isDestroyed()) {
      this.rightApi.removeRowDropZone(this.rightDropZone);
    }
    this.leftDropZone = null;
    this.rightDropZone = null;
  }

  addGridDropZone(side, api: GridApi): RowDropZoneParams {
    const dropApi = side === 'Left' ? this.rightApi : this.leftApi;
    const dropZone = dropApi.getRowDropZoneParams({
      onDragStop: function (params) {

      }
    });

    api.addRowDropZone(dropZone);
    return dropZone;
  }
}

export interface IStandardConfigDtoState {
  IStandardConfigDto: IStandardConfigDto;
  AllIStandardConfigItemDtos: Array<IStandardConfigItemDto>;
}